import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { EMAIL_DOMAIN } from '@/lib/cloud';

const isCustomEmail = (value: string) =>
  value.trim().toLowerCase().endsWith(`@${EMAIL_DOMAIN}`);

const RESEND_COOLDOWN_SECONDS = 30;

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [needConfirm, setNeedConfirm] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    setNeedConfirm(false);

    let loginEmail = email.trim().toLowerCase();

    // Si l'utilisateur saisit son adresse personnalisée @worldapeiron.com,
    // on la résout vers son email de connexion réel.
    if (isCustomEmail(loginEmail)) {
      try {
        const { data: resolveData, error: resolveError } = await supabase.functions.invoke(
          'resolve-login-email',
          { body: { email: loginEmail } },
        );
        if (resolveError || !resolveData?.email) {
          setStatus('error');
          setErrorMsg(
            "Cette adresse @worldapeiron.com n'est liée à aucun compte. Vérifiez-la, ou connectez-vous avec l'email utilisé lors de votre inscription.",
          );
          return;
        }
        loginEmail = resolveData.email;
      } catch {
        setStatus('error');
        setErrorMsg("Impossible de résoudre cette adresse pour le moment. Réessayez.");
        return;
      }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password,
    });

    if (error) {
      setStatus('error');
      const msg = error.message.toLowerCase();
      if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
        setNeedConfirm(true);
        setErrorMsg(
          "Votre adresse email n'est pas encore vérifiée. Vérifiez votre boîte de réception ou renvoyez un email de confirmation.",
        );
      } else if (msg.includes('invalid login credentials') || msg.includes('invalid')) {
        setErrorMsg('Email ou mot de passe incorrect.');
      } else {
        setErrorMsg(error.message);
      }
      return;
    }

    if (data.session) {
      navigate(from && from.startsWith('/') && from !== '/auth/login' ? from : '/dashboard', {
        replace: true,
      });
    } else {
      setStatus('error');
      setErrorMsg('Connexion impossible pour le moment. Veuillez réessayer.');
    }
  };

  const handleResend = async () => {
    setResending(true);
    setErrorMsg('');
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    setResending(false);
    if (error) {
      setErrorMsg(error.message);
    } else {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setErrorMsg('Un nouvel email de confirmation a été envoyé. Vérifiez votre boîte de réception.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12 md:py-20">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
              alt="WorldApeiron"
              className="h-14 w-14 mx-auto mb-4 object-contain"
            />
            <h1 className="font-heading font-bold text-2xl text-foreground-50">
              Connexion
            </h1>
            <p className="text-sm text-foreground-400 mt-2">
              Accédez à votre espace entreprise
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-400 mb-1.5">
                Adresse email
              </label>
              <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-foreground-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                />
              </div>
              <p className="text-xs text-foreground-400 mt-1.5">
                Vous pouvez aussi vous connecter avec votre adresse @{EMAIL_DOMAIN}.
              </p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-400 mb-1.5">
                Mot de passe
              </label>
              <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-lock-line text-foreground-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="w-10 h-10 flex items-center justify-center shrink-0 text-foreground-400 hover:text-foreground-400"
                  aria-label={showPassword ? 'Masquer' : 'Afficher'}
                >
                  <i className={showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} />
                </button>
              </div>
            </div>

            {/* Honeypot */}
            <div className="website_alt_field">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {/* Error */}
            {status === 'error' && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                {errorMsg}
                {needConfirm && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending || resendCooldown > 0}
                    className="block mt-2 text-primary-400 hover:text-primary-300 font-medium disabled:opacity-60"
                  >
                    {resending
                      ? 'Envoi en cours...'
                      : resendCooldown > 0
                        ? `Renvoyer l'email de confirmation (${resendCooldown}s)`
                        : "Renvoyer l'email de confirmation"}
                  </button>
                )}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {status === 'loading' ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center space-y-3">
            <Link
              to="/auth/forgot-password"
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              Mot de passe oublié ?
            </Link>
            <p className="text-sm text-foreground-400">
              Pas encore de compte ?{' '}
              <Link
                to="/auth/register"
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
              >
                Inscrire mon entreprise
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Login;